import { useState } from 'react';
import { Grid, Typography, Slider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UseMutateFunction } from 'react-query';
import { useParams } from 'react-router-dom';
import getAbilityForReservation from 'pages/calculator/MediaPlanning/Plan/PlanSummary/SaveBookingFlow/helpers/getAbilityForReservation';
import ChangingDefaultPresetsInfo from '../ChangingDefaultPresetsInfo/ChangingDefaultPresetsInfo';
import WeeklyDatePicker from '../../../../common/WeeklyDatePicker/WeeklyDatePicker';
import usePlanStore from '../../../../../../../store/planStore';
import {
  DefaultSelect,
  Item,
} from '../../../../common/DefaultSelect/DefaultSelect';
import convertDecimalToPercentValue from '../../../../Plan/Countries/utils/convertDecimalToPercentValue';
import { MetaTouchpointSetup } from '../../interfaces';
import GoldenRuleBox from '../GoldenRuleBox/GoldenRuleBox';
import renderSaturation from '../../TikTokTouchpoint/TargetGroupSelectors/utils/renderSaturation';
import getSaturationNullish from '../../utils/getSaturationNullish';
import getSaturationMinEqualToMax from '../../utils/getSaturationMinEqualToMax';
import {
  ApiResponse,
  MetaFanpage,
} from '../../../../../../../api/services/mediaPlanning/types';
import { ObjectSelect } from '../../../../common/ObjectSelect/ObjectSelect';
import { useAuthContext } from '../../../../../../../context/AuthContext';
import NoMetaEditRightsInfoBox from '../NoMetaEditRightsInfoBox/NoMetaEditRightsInfoBox';
import HelperTooltip from '../../../../../../../shared/HelperTooltip/HelperTooltip';
import linksConfig from '../../../../../../../config/linking';

interface TargetGroupSelectorsProps {
  targetGroupSaturation: number;
  startDate?: string | null;
  fetchedCampaignLengthsData?: Item[];
  fetchedFanpagesData?: MetaFanpage[];
  handleChangeStateValues: (
    key: string,
    value: number | string | object
  ) => void;
  minMaxSelectorsValues?: MetaTouchpointSetup;
  fetchMetaConfig: UseMutateFunction<
    ApiResponse<MetaTouchpointSetup>,
    unknown,
    Item | undefined,
    unknown
  >;
  isMetaRF: boolean;
}

function TargetGroupSelectors({
  targetGroupSaturation,
  fetchedCampaignLengthsData,
  fetchedFanpagesData,
  handleChangeStateValues,
  startDate,
  minMaxSelectorsValues,
  fetchMetaConfig,
  isMetaRF,
}: TargetGroupSelectorsProps) {
  const { t } = useTranslation();
  const { id } = useParams();
  const { alreadySavedPlanId } = usePlanStore.getState();
  const [isDefaultTargetGroupChanged] = useState(false);

  const { countryForms, selectedCountry } = usePlanStore();

  const { metaConfig } = countryForms[selectedCountry!];
  const isTargetGroupsError = metaConfig?.error && !metaConfig?.targetGroup;

  const isCampaignLengthError =
    !metaConfig?.campaignLength && metaConfig?.error;

  const countryForm = countryForms[selectedCountry!];

  const { userRoles } = useAuthContext();

  return (
    <Grid container rowGap={5}>
      {(countryForm.metaConfig?.reservationStatus === 'reserved' ||
        countryForm.metaConfig?.reservationStatus === 'partially-reserved') &&
        (!!id || !!alreadySavedPlanId) &&
        !getAbilityForReservation(userRoles) && (
          <Grid item xs={12}>
            <NoMetaEditRightsInfoBox />
          </Grid>
        )}
      <Grid item xs={6} sx={{ pr: '24px' }}>
        {isMetaRF && (
          <ObjectSelect<MetaFanpage>
            value={
              metaConfig?.campaignDestination
                ? {
                    value: metaConfig.campaignDestination,
                    name: metaConfig.campaignDestination.fbPage,
                  }
                : null
            }
            onChange={(newValue) =>
              handleChangeStateValues(
                'campaignDestination',
                newValue.value ?? ''
              )
            }
            items={
              fetchedFanpagesData?.map((fanpage) => {
                return {
                  name: fanpage.fbPage,
                  value: fanpage || {
                    fbPage: null,
                    fbPageId: null,
                    igPage: null,
                    igPageId: null,
                  },
                };
              }) || []
            }
            getOptionKey={(item) => item.fbPage}
            selectName={t('mediaPlanning.fanpage')}
            labelId="fanpage"
            disabled={
              (!!metaConfig?.campaignDestination &&
                fetchedFanpagesData?.length === 1) ||
              countryForm.metaConfig?.reservationStatus === 'reserved' ||
              countryForm.metaConfig?.reservationStatus ===
                'partially-reserved' ||
              ((!!id || !!alreadySavedPlanId) &&
                !getAbilityForReservation(userRoles))
            }
          />
        )}
      </Grid>
      <Grid item xs={2}>
        <HelperTooltip
          tooltipData={linksConfig.seeGoldenRules}
          sx={{ marginLeft: '12px', alignSelf: 'flex-start' }}
        />
      </Grid>
      <Grid item xs={4} />
      <Grid item xs={3} sx={{ pr: '24px' }}>
        <DefaultSelect
          value={metaConfig?.targetGroup ?? ''}
          onChange={(newValue) => {
            handleChangeStateValues('targetGroup', newValue.value ?? '');
            fetchMetaConfig(newValue);
          }}
          items={
            countryForm.metaConfig?.targetGroups?.map((group) => {
              return {
                name:
                  group === countryForm.metaConfig?.recommendedTargetGroup
                    ? `${group} (recommended)`
                    : group,
                value: group,
              };
            }) || []
          }
          selectName={t('mediaPlanning.targetGroup')}
          labelId="target-group"
          error={metaConfig?.isGoldenRuleMet === false || isTargetGroupsError}
          errorMessage={
            isTargetGroupsError ? t('mediaPlaning.errors.targetGroup') : null
          }
          disabled={
            countryForm.metaConfig?.reservationStatus === 'reserved' ||
            countryForm.metaConfig?.reservationStatus ===
              'partially-reserved' ||
            ((!!id || !!alreadySavedPlanId) &&
              !getAbilityForReservation(userRoles))
          }
        />
      </Grid>
      {metaConfig?.isGoldenRuleMet === false ? (
        <GoldenRuleBox />
      ) : (
        <>
          <Grid
            item
            xs={3}
            sx={{ pr: '24px', display: 'flex', flexDirection: 'column' }}
          >
            <Typography variant="caption" lineHeight={1}>
              {renderSaturation(
                t('mediaPlanning.targetGroupSaturation'),
                targetGroupSaturation,
                {
                  min: minMaxSelectorsValues?.saturationMin,
                  max: minMaxSelectorsValues?.saturationMax,
                  default: minMaxSelectorsValues?.saturationDefault,
                }
              )}
            </Typography>
            <Slider
              valueLabelDisplay="auto"
              step={0.05}
              value={targetGroupSaturation ?? 0}
              min={minMaxSelectorsValues?.saturationMin ?? 0}
              max={minMaxSelectorsValues?.saturationMax ?? 1}
              slots={{
                track: getSaturationMinEqualToMax(minMaxSelectorsValues)
                  ? () => null
                  : undefined,
              }}
              marks={
                minMaxSelectorsValues?.saturationMin !== null && [
                  {
                    value: minMaxSelectorsValues?.saturationMin ?? 0,
                    label: convertDecimalToPercentValue(
                      minMaxSelectorsValues?.saturationMin
                    ),
                  },
                  ...(minMaxSelectorsValues?.saturationMin !==
                  minMaxSelectorsValues?.saturationMax
                    ? [
                        {
                          value: minMaxSelectorsValues?.saturationMax ?? 1,
                          label: convertDecimalToPercentValue(
                            minMaxSelectorsValues?.saturationMax
                          ),
                        },
                      ]
                    : []),
                ]
              }
              valueLabelFormat={(value) =>
                `${convertDecimalToPercentValue(value).toFixed(0)}%`
              }
              onChange={(_, newValue) =>
                handleChangeStateValues('saturation', +newValue)
              }
              disabled={
                getSaturationMinEqualToMax(minMaxSelectorsValues) ||
                getSaturationNullish(minMaxSelectorsValues) ||
                countryForm.metaConfig?.reservationStatus === 'reserved' ||
                countryForm.metaConfig?.reservationStatus ===
                  'partially-reserved' ||
                ((!!id || !!alreadySavedPlanId) &&
                  !getAbilityForReservation(userRoles))
              }
            />
          </Grid>
          <Grid item xs={3} sx={{ pr: '24px' }}>
            <WeeklyDatePicker
              label={t('mediaPlanning.startDate')}
              value={startDate || undefined}
              setState={(value) => handleChangeStateValues('startDate', value)}
              disabled={
                countryForm.metaConfig?.reservationStatus === 'reserved' ||
                countryForm.metaConfig?.reservationStatus ===
                  'partially-reserved' ||
                ((!!id || !!alreadySavedPlanId) &&
                  !getAbilityForReservation(userRoles))
              }
            />
          </Grid>
          <Grid item xs={3} sx={{ pr: '24px' }}>
            <DefaultSelect
              value={metaConfig?.campaignLength ?? ''}
              onChange={(newValue) =>
                handleChangeStateValues('campaignLength', newValue.value ?? '')
              }
              items={fetchedCampaignLengthsData}
              selectName={t('mediaPlanning.campaignLength')}
              labelId="target-group"
              error={isCampaignLengthError}
              errorMessage={t('mediaPlanning.errors.campaignLength')}
              disabled={
                countryForm.metaConfig?.reservationStatus === 'reserved' ||
                countryForm.metaConfig?.reservationStatus ===
                  'partially-reserved' ||
                ((!!id || !!alreadySavedPlanId) &&
                  !getAbilityForReservation(userRoles))
              }
            />
          </Grid>
          {isDefaultTargetGroupChanged && (
            <Grid item xs={12}>
              <ChangingDefaultPresetsInfo />
            </Grid>
          )}
        </>
      )}
    </Grid>
  );
}

export default TargetGroupSelectors;
